import { FC } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { format, isValid } from 'date-fns';

import { formatCurrencyDecimals, formatNumber } from 'utils/formats';
import { dateFormat } from 'constants/formats';

import { isPlanClassVisible } from 'modules/incentivesMarket/utils/isPlanClassVisible';
import { usePublishIncentiveOfferModal } from '../context/PublishIncentiveOfferModalContext';

const PublishIncentiveOfferModalStep1: FC = () => {
  const { t } = useTranslate();
  const { data } = usePublishIncentiveOfferModal();

  const isSellOffer = data.incentiveOffer?.type === 'SELL';
  const isBuyOffer = data.incentiveOffer?.type === 'BUY';
  const className = data.incentiveOffer?.className || '';
  const unitsCount = data.incentiveOffer?.unitsCount?.total || 0;
  const unitPrice = data.incentiveOffer?.unitPrice || 0;
  const totalOfferPrice = unitPrice * unitsCount;

  const offerTypeLabel = isSellOffer ? t('Sale') : t('Buy');

  const partialAcceptanceLabel = data.incentiveOffer?.isPartialAmountEnabled
    ? t('Yes')
    : t('Nope');

  const formatDateValue = (date?: string | Date) => {
    if (!date || !isValid(new Date(date))) return '-';
    return format(new Date(date), dateFormat);
  };

  const validityLabel = `${formatDateValue(
    data.incentiveOffer?.startDate
  )} – ${formatDateValue(data.incentiveOffer?.endDate)}`;

  return (
    <div className="nk-stepper-step active">
      <div className="text-center mb-4">
        <em className="icon ni ni-shield-check fs-24px text-primary" />
        <h5 className="mt-2">{t('ConfirmYourOfferDetails')}</h5>
        <p className="text-soft">{t('ReviewCarefullyBeforeConfirming')}</p>
      </div>

      <div className="card card-bordered bg-light">
        <div className="card-inner">
          <div className="row">
            <div className="col-6 my-1">
              <span className="text-soft d-block fs-12px">
                {t('OfferType')}
              </span>
              <strong>{offerTypeLabel}</strong>
            </div>

            <div className="col-6 my-1">
              {isPlanClassVisible() && (
                <>
                  <span className="text-soft d-block fs-12px">
                    {t('IncentivesMarketUnitClass')}
                  </span>
                  <strong>{className || '-'}</strong>
                </>
              )}
            </div>

            <div className="col-6 my-1">
              <span className="text-soft d-block fs-12px">
                {t('IncentivesMarketUnitsNumber')}
              </span>
              <strong className="text-primary">
                {formatNumber(unitsCount)} {t('IncentivesMarketUnits').toLowerCase()}
              </strong>
            </div>
            <div className="col-6 my-1">
              <span className="text-soft d-block fs-12px">
                {t('IncentivesMarketPricePerUnit')}
              </span>
              <strong>{formatCurrencyDecimals(unitPrice)}</strong>
            </div>
            <div className="col-6 my-1">
              <span className="text-soft d-block fs-12px">
                {t('TotalAmount')}
              </span>
              <strong className="text-primary fs-16px">
                {formatCurrencyDecimals(totalOfferPrice)}
              </strong>
            </div>
            <div className="col-6 my-1">
              <span className="text-soft d-block fs-12px">{t('Validity')}</span>
              <strong>{validityLabel}</strong>
            </div>
            {isBuyOffer && (
              <div className="col-6 my-1">
                <span className="text-soft d-block fs-12px">
                  {t('EligibleCalculationDate')}
                </span>
                <strong>
                  {formatDateValue(data.incentiveOffer?.calculationDate)}
                </strong>
              </div>
            )}
            <div className="col-6 my-1">
              <span className="text-soft d-block fs-12px">
                {t('PartialAcceptance')}
              </span>
              <strong>{partialAcceptanceLabel}</strong>
            </div>
            <div className="col-6 my-1">
              <span className="text-soft d-block fs-12px">
                {t('Visibility')}
              </span>
              <strong>{t('IncentivesMarketAllBeneficiaries')}</strong>
            </div>
          </div>
        </div>
      </div>

      <div className="alert alert-warning alert-icon mt-3">
        <em className="icon ni ni-alert-circle" />
        <p className="mb-0 fs-13px">
          {isSellOffer
            ? t('IncentivesMarketUnitsReservedWarning', { count: formatNumber(unitsCount) })
            : t('PublishOfferWarning')}
        </p>
      </div>
    </div>
  );
};

export default PublishIncentiveOfferModalStep1;
